import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  addEdge,
  MiniMap,
  Controls,
  Background,
  Panel,
  Node as RFNode,
  Edge as RFEdge,
  NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { getMindMapNotes } from '../../api/notes';
import { MindMapNote } from '../../types/mind-map-note';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FaFileAlt } from 'react-icons/fa';
import { FaExternalLinkAlt } from 'react-icons/fa';
import MindMapTurboNode from '../ui/turbo/MindMapTurboNode';
import './mind-map.css';
import '../ui/turbo/turbo.css';

const nodeTypes: NodeTypes = {
  turbo: MindMapTurboNode as any,
};

const initialElements = (notes: MindMapNote[]) => {
  const nodes: RFNode[] = notes.map((note) => ({
    id: note.id.toString(),
    type: 'turbo',
    data: {
      label: (
        <Link to={`/notes/${note.id}`} target="_blank">
          {note.name}
          <FaExternalLinkAlt style={{ fontSize: '10px' }} />
        </Link>
      ),
      icon: (<FaFileAlt />),
    },
    position: {
      x: Math.random() * 1500,
      y: Math.random() * 1500,
    },
  }));

  const edges: RFEdge[] = [];
  notes.forEach((note) => {
    note.related_notes.forEach((relatedId) => {
      const edgeId = `${note.id}-${relatedId}`;
      const reverseEdgeId = `${relatedId}-${note.id}`;

      if (!edges.find((e) => e.id === edgeId || e.id === reverseEdgeId)) {
        edges.push({
          id: edgeId,
          source: note.id.toString(),
          target: relatedId.toString(),
          type: 'smoothstep',
          style: { stroke: '#1a192b', strokeWidth: 2 },
        });
      }
    });
  });

  return { nodes, edges };
};

const MindMap: React.FC = () => {
  const [notes, setNotes] = useState<MindMapNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const [nodes, setNodes, onNodesChange] = useNodesState<RFNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<RFEdge>([]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await getMindMapNotes();
      setNotes(response.notes);
    } catch (err) {
      setError(t('common.errorFetchingData'));
      console.error('Error fetching mind map notes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, []);

  useEffect(() => {
    if (notes.length > 0) {
      const { nodes: newNodes, edges: newEdges } = initialElements(notes);
      setNodes(newNodes);
      setEdges(newEdges);
    }
  }, [notes, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: any) => setEdges((els) => addEdge(params, els)),
    [setEdges]
  );

  const updatePos = useCallback(() => {
    setNodes((nds) => {
      return nds.map((node) => {
        return {
          ...node,
          position: {
            x: Math.random() * 1500,
            y: Math.random() * 1500,
          },
        };
      });
    });
  }, [setNodes]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Mind Map
      </Typography>
      <Box style={{ height: '600px', width: '100%' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={{
            type: 'smoothstep',
            markerEnd: 'edge-circle',
          }}
          fitView
          minZoom={0}
          className="turbo-flow"
        >
          <MiniMap />
          <Controls />
          <Background />
          <Panel position="top-right">
            <Button 
              onClick={updatePos} 
              variant="contained"
              sx={{
                backgroundColor: '#1a192b',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.3px',
                textTransform: 'none',
                minWidth: '140px',
                boxShadow: '0 2px 8px rgba(26, 25, 43, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#2a2a3a',
                  boxShadow: '0 4px 12px rgba(26, 25, 43, 0.4)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  backgroundColor: '#0a0a1a',
                  boxShadow: '0 1px 4px rgba(26, 25, 43, 0.3)',
                  transform: 'translateY(0)',
                },
                '&:focus': {
                  outline: '2px solid rgba(42, 138, 246, 0.5)',
                  outlineOffset: '2px',
                },
              }}
            >
              {t('common.changePositions')}
            </Button>
          </Panel>
          <svg>
            <defs>
              <linearGradient id="edge-gradient">
                <stop offset="0%" stopColor="#ae53ba" />
                <stop offset="100%" stopColor="#2a8af6" />
              </linearGradient>

              <marker
                id="edge-circle"
                viewBox="-5 -5 10 10"
                refX="0"
                refY="0"
                markerUnits="strokeWidth"
                markerWidth="10"
                markerHeight="10"
                orient="auto"
              >
                <circle stroke="#2a8af6" strokeOpacity="0.75" r="2" cx="0" cy="0" />
              </marker>
            </defs>
          </svg>
        </ReactFlow>
      </Box>
    </Box>
  );
};

export default MindMap;

